import { CONSTANTS } from './constants.js';
import { PURCHASE_THRESHOLDS, getScenarioThresholds } from '../config/purchaseThresholds.js';

// Forbedret demand manager - maksimal overskudsvarme udnyttelse
class ImprovedDemandManager {
  constructor(storageManager, priorityManager, config = null) {
    this.storage = storageManager;
    this.priority = priorityManager;
    this.thresholds = config ? getScenarioThresholds(config) : PURCHASE_THRESHOLDS;
  }

  /**
   * Hent lager niveauer og plads
   * @returns {Object} Storage levels and space information
   */
  getStorageInfo() {
    const levels = this.storage ? this.storage.getLevels() : null;
    if (!levels) {
      console.error('No storage levels available!');
      return null;
    }
    
    const space = this.storage.getAvailableSpace();
    return { levels, space };
  }

  /**
   * Helper: Lever demand fra LTES (An-værk produktion)
   * PRIORITY 1: Always use storage first before any heater activation
   * @param {number} demand - Heat demand in MWh
   * @param {Object} levels - Storage levels
   * @param {number} currentHour - Current simulation hour for thermal updates
   * @returns {Object} Result with anProduction and actions
   */
  deliverFromStorage(demand, levels, currentHour = 0) {
    try {
      const actions = [];
      let anProduction = 0;
      let remainingDemand = demand;
      
      // STEP 1: Use LTES first (cheapest - already paid for)
      // NO DISCHARGE LOSS - efficiency is 1.0
      const ltesAvailable = levels.ltes * this.storage.storage.ltes.capacity;
      
      // Since there's no discharge loss, we can deliver exactly what's available
      const ltesToDischarge = Math.min(remainingDemand, ltesAvailable);

      // PERFORMANCE: Comment out verbose logging
      // console.log('LTES available:', ltesAvailable, 'Will discharge:', ltesToDischarge);
      
      if (ltesToDischarge > 0.01) {
        const discharged = this.storage.dischargeLTES(ltesToDischarge);

        // Update thermal physics if available
        if (this.thermalPhysics) {
          this.thermalPhysics.updateLTESDischarge(ltesToDischarge, currentHour);
        }

        // NO LOSS - discharged amount equals An-værk production
        anProduction = discharged;
        remainingDemand -= anProduction;
        
        // Calculate Ab-værk and Net delivery with proper losses:
        // An-værk → Ab-værk: 3% production loss (efficiency = 0.97)
        // Ab-værk → NET: 10% grid loss (efficiency = 0.90)
        const abPlantDelivery = anProduction * (1 - CONSTANTS.EFFICIENCY.production_loss);
        const netDeliveryAmount = abPlantDelivery * (1 - CONSTANTS.EFFICIENCY.grid_loss);

        actions.push({
          type: 'DISCHARGE_LTES_TO_DH',
          amount: discharged,
          anPlant: anProduction,
          abPlant: abPlantDelivery,  // 3% production loss applied
          netDelivery: netDeliveryAmount,  // 10% grid loss on top of production loss
          reason: 'Primary: Direct LTES discharge (3% prod loss + 10% grid loss)'
        });
      }
      
      // STEP 2: Brug HTES → Turbine → DIREKTE til demand
      // VIGTIGT: Turbine varme leveres DIREKTE til demand, IKKE via LTES!
      // Dette undgår dobbelt-tælling (turbine varme tælles som anProduction NU, ikke når LTES discharges senere)
      if (remainingDemand > 0.1 && levels.htes > 0.02) {
        const htesAvailable = (levels.htes - 0.02) * this.storage.storage.htes.capacity;

        // Beregn hvor meget HTES vi skal bruge for at dække remaining demand via turbine
        const htesNeededForDemand = remainingDemand / CONSTANTS.EFFICIENCY.turbine_heat;
        const htesDischarge = Math.min(htesNeededForDemand, htesAvailable);

        if (htesDischarge > 0.1) {
          const discharged = this.storage.dischargeHTES(htesDischarge);

          // Update thermal physics
          if (this.thermalPhysics) {
            this.thermalPhysics.updateHTESDischarge(discharged, currentHour);
          }

          // Turbine output
          const turbineHeat = discharged * CONSTANTS.EFFICIENCY.turbine_heat;
          const turbineEl = discharged * CONSTANTS.EFFICIENCY.turbine_electric;

          // Lever varme DIREKTE til demand (ikke via LTES buffer)
          // Dette sikrer korrekt tælling: varme leveres NU, ikke gemt til senere
          const deliveredHeat = Math.min(turbineHeat, remainingDemand);
          anProduction += deliveredHeat;
          remainingDemand -= deliveredHeat;

          // Evt. overskudsvarme går til LTES buffer (til senere brug)
          const excessHeat = turbineHeat - deliveredHeat;
          if (excessHeat > 0.01) {
            this.storage.chargeLTES(excessHeat);
            if (this.thermalPhysics) {
              this.thermalPhysics.updateLTESCharge(excessHeat, currentHour);
            }
          }

          // Calculate losses for delivered production
          const abPlantFromTurbine = deliveredHeat * (1 - CONSTANTS.EFFICIENCY.production_loss);
          const netFromTurbine = abPlantFromTurbine * (1 - CONSTANTS.EFFICIENCY.grid_loss);

          actions.push({
            type: 'DISCHARGE_HTES_TO_LTES_FOR_DEMAND',
            input: discharged,
            heat: turbineHeat,
            electricity: turbineEl,
            deliveredHeat: deliveredHeat,
            excessToLTES: excessHeat,
            anPlant: deliveredHeat,
            abPlant: abPlantFromTurbine,
            netDelivery: netFromTurbine,
            reason: `HTES→Turbine→Demand: ${deliveredHeat.toFixed(2)} MWh varme, ${turbineEl.toFixed(2)} MWh el`
          });
        }
      }

      return { anProduction, actions, remainingDemand };
    } catch (error) {
      console.error('Error in deliverFromStorage:', error);
      return { anProduction: 0, actions: [], remainingDemand: demand };
    }
  }

  /**
   * Helper: Håndter overskudsvarme fra fabrik
   * @param {number} wasteHeatMW - Available waste heat in MW
   * @param {Object} levels - Storage levels
   * @param {Object} space - Available storage space
   * @param {number} spotPrice - Current spot price
   * @returns {Object} Result with cost, revenue and actions
   */
  handleWasteHeat(wasteHeatMW, levels, space, spotPrice, currentHour = 0) {
    try {
      const actions = [];
      let totalCost = 0;
      let totalRevenue = 0;

      // GRID-ONLY MODE: Skip waste heat if not available (will return 0 from scaled provider)
      if (wasteHeatMW <= 0) {
        return { totalCost, totalRevenue, actions };
      }
      
      // Run turbine only when really needed for space
      if (levels.ltes >= 0.95 && levels.htes > 0.20 && wasteHeatMW > 8 && spotPrice > 200) {  // Very strict
        // Calculate how much space we need
        const spaceNeeded = wasteHeatMW * 1.1; // Small buffer
        const currentSpace = (0.99 - levels.ltes) * this.storage.storage.ltes.capacity;
        
        if (currentSpace < spaceNeeded) {
          const htesAvailable = (levels.htes - 0.05) * this.storage.storage.htes.capacity;
          // Only discharge what we need for space
          const htesNeeded = (spaceNeeded - currentSpace) / CONSTANTS.EFFICIENCY.turbine_heat;
          const htesDischarge = Math.min(htesNeeded, htesAvailable, 5); // Max 5MW for space creation
          
          // Check if HTES can discharge thermally
          const canDischargeHTES = !this.thermalPhysics || this.thermalPhysics.canDischargeHTES();
          if (htesDischarge > 0.5 && spotPrice > 100 && canDischargeHTES) { // Only if price is reasonable
            const discharged = this.storage.dischargeHTES(htesDischarge);

            // Get dynamic turbine efficiency
            let turbineEfficiency = { heat: CONSTANTS.EFFICIENCY.turbine_heat, electricity: CONSTANTS.EFFICIENCY.turbine_electric };
            if (this.thermalPhysics) {
              turbineEfficiency = this.thermalPhysics.getTurbineEfficiency();
              this.thermalPhysics.updateHTESDischarge(htesDischarge, currentHour);
            }

            const heat = discharged * turbineEfficiency.heat;
            const electricity = discharged * turbineEfficiency.electricity;
            const storedInLTES = this.storage.chargeLTES(heat);

            if (this.thermalPhysics) {
              this.thermalPhysics.updateLTESCharge(heat, currentHour);
            }
            totalRevenue += electricity * spotPrice;
            
            actions.push({
              type: 'DISCHARGE_HTES_TURBINE_FOR_WASTE',
              input: discharged,
              heat: heat,
              electricity: electricity,
              storedInLTES: storedInLTES,
              revenue: electricity * spotPrice,
              reason: `Smart: Creating ${(spaceNeeded - currentSpace).toFixed(1)} MWh space for waste heat`
            });
          }
        }
      }
      
      // Accept ALL available waste heat - it's the cheapest source!
      const updatedSpace = this.storage.getAvailableSpace();
      const wasteToLTES = Math.min(wasteHeatMW, updatedSpace.ltes);
      
      // ALWAYS accept waste heat - it's the cheapest source at 150 DKK/MWh!
      // But check thermal constraints
      const canChargeLTES = !this.thermalPhysics || this.thermalPhysics.canChargeLTES();
      if (wasteToLTES > 0.001 && canChargeLTES) {
        const stored = this.storage.chargeLTES(wasteToLTES);

        if (this.thermalPhysics) {
          this.thermalPhysics.updateLTESCharge(stored, currentHour);
        }

        totalCost += wasteToLTES * CONSTANTS.FACTORY_WASTE_HEAT_PRICE;
        
        actions.push({
          type: 'CHARGE_LTES_WASTE_HEAT',
          amount: wasteToLTES,
          stored: stored,
          cost: wasteToLTES * CONSTANTS.FACTORY_WASTE_HEAT_PRICE,
          reason: 'Waste heat at 150 DKK - ALWAYS accept!'
        });
        this.priority.markLTESChargeAny();
      }
      
      // SMART: Only force turbine for significant waste heat amounts
      const unacceptedWaste = wasteHeatMW - wasteToLTES;
      if (unacceptedWaste > 1.0 && spotPrice > 50) { // Only for significant amounts and reasonable prices
        // Check if turbine operation is economical
        const wasteHeatValue = unacceptedWaste * CONSTANTS.FACTORY_WASTE_HEAT_PRICE;
        const electricityValue = unacceptedWaste * 0.15 * spotPrice; // Approx electricity revenue
        
        if (levels.htes > 0.05 && electricityValue > wasteHeatValue * 0.5) { // Only if economical
          const htesNeeded = unacceptedWaste / CONSTANTS.EFFICIENCY.turbine_heat;
          const htesDischarge = Math.min(5, htesNeeded, (levels.htes - 0.05) * this.storage.storage.htes.capacity);
          
          if (htesDischarge > 0.5) {
            const discharged = this.storage.dischargeHTES(htesDischarge);
            const heat = discharged * CONSTANTS.EFFICIENCY.turbine_heat;
            const electricity = discharged * CONSTANTS.EFFICIENCY.turbine_electric;
            
            // Store heat in LTES to make space
            const heatStored = this.storage.chargeLTES(heat);
            totalRevenue += electricity * spotPrice;
            
            actions.push({
              type: 'DISCHARGE_HTES_ECONOMICAL_WASTE',
              input: discharged,
              heat: heat,
              electricity: electricity,
              storedInLTES: heatStored,
              revenue: electricity * spotPrice,
              reason: `Economical: Space for ${unacceptedWaste.toFixed(1)} MWh waste @ ${spotPrice.toFixed(0)} DKK`
            });
            
            // Now try to accept remaining waste heat
            const newSpace = this.storage.getAvailableSpace();
            const additionalWaste = Math.min(unacceptedWaste, newSpace.ltes);
            if (additionalWaste > 0.1) {
              const stored = this.storage.chargeLTES(additionalWaste);
              totalCost += additionalWaste * CONSTANTS.FACTORY_WASTE_HEAT_PRICE;
              actions.push({
                type: 'CHARGE_LTES_WASTE_HEAT_RECOVERED',
                amount: additionalWaste,
                stored: stored,
                cost: additionalWaste * CONSTANTS.FACTORY_WASTE_HEAT_PRICE,
                reason: 'Recovered waste heat after space creation'
              });
            }
          }
        }
      }
      
      return { totalCost, totalRevenue, actions };
    } catch (error) {
      console.error('Error in handleWasteHeat:', error);
      return { totalCost: 0, totalRevenue: 0, actions: [] };
    }
  }

  /**
   * Helper: Håndter sol energi med maksimal prioritet
   * @param {number} solarMWh - Available solar energy
   * @param {number} spotPrice - Current spot price
   * @param {Object} levels - Storage levels
   * @param {number} maxHeater - Maximum heater capacity
   * @param {number} unmetDemand - Remaining unmet demand
   * @returns {Object} Result with cost, revenue, heaterUsed and actions
   */
  handleSolarEnergy(solarMWh, spotPrice, levels, maxHeater, unmetDemand = 0, hour = 12, currentHour = 0) {
    try {
      const actions = [];
      let totalCost = 0;
      let totalRevenue = 0;
      let heaterUsed = 0;

      // GRID-ONLY MODE: Skip solar if not available (will return 0 from scaled provider)
      if (solarMWh <= 0) {
        return { totalCost, totalRevenue, heaterUsed, solarUsed: 0, storedForDemand: 0, actions };
      }
      
      const getHeaterAvailable = () => Math.max(0, maxHeater - this.priority.heaterUsedCapacity);
      const reserveHeaterFor = (mode, amount) => {
        return this.priority.requestHeaterUse(mode, amount);
      };
      
      // CONSERVATIVE: Only make space for solar if critically needed
      const space = this.storage.getAvailableSpace();
      if (levels.ltes > 0.90 && levels.htes > 0.15 && solarMWh > 10 && spotPrice < 100) {  // Much stricter
        // Calculate space needed for solar
        const spaceNeeded = Math.min(solarMWh, 30); // Up to heater capacity
        const currentSpace = space.ltes;
        
        if (currentSpace < spaceNeeded * 0.8) { // Only if we really need space
          // Check if turbine operation makes sense
          const solarValue = solarMWh * spotPrice; // Value of solar we can accept
          const turbineElectricity = (spaceNeeded - currentSpace) / CONSTANTS.EFFICIENCY.turbine_heat * CONSTANTS.EFFICIENCY.turbine_electric;
          const electricityRevenue = turbineElectricity * spotPrice;
          
          if (electricityRevenue > solarValue * 0.3 || spotPrice > 200) { // Only if economical
            const htesNeeded = (spaceNeeded - currentSpace) / CONSTANTS.EFFICIENCY.turbine_heat;
            const htesDischarge = Math.min(htesNeeded, (levels.htes - 0.05) * this.storage.storage.htes.capacity, 5);
            
            if (htesDischarge > 1.0) {
              // Check if HTES can discharge thermally
              if (this.thermalPhysics && !this.thermalPhysics.canDischargeHTES()) {
                // Skip HTES discharge if temperature too low
              } else {
                const discharged = this.storage.dischargeHTES(htesDischarge);

                // Get dynamic turbine efficiency
                let turbineEfficiency = { heat: CONSTANTS.EFFICIENCY.turbine_heat, electricity: CONSTANTS.EFFICIENCY.turbine_electric };
                if (this.thermalPhysics) {
                  turbineEfficiency = this.thermalPhysics.getTurbineEfficiency();
                  this.thermalPhysics.updateHTESDischarge(htesDischarge, currentHour);
                }

                const heat = discharged * turbineEfficiency.heat;
                const electricity = discharged * turbineEfficiency.electricity;
                const storedInLTES = this.storage.chargeLTES(heat);

                if (this.thermalPhysics) {
                  this.thermalPhysics.updateLTESCharge(heat, currentHour);
                }

                totalRevenue += electricity * spotPrice;

                actions.push({
                  type: 'DISCHARGE_HTES_FOR_SOLAR_SPACE',
                  input: discharged,
                  heat: heat,
                  electricity: electricity,
                  storedInLTES: storedInLTES,
                  revenue: electricity * spotPrice,
                  reason: `Smart: ${spaceNeeded.toFixed(1)} MWh space for solar @ ${spotPrice.toFixed(0)} DKK`
                });
              }
            }
          }
        }
      }
      
      let solarUsed = 0;

      // ============================================================
      // HELIAC FLOW: Sol → Kedel → HTES → Turbine → LTES → Demand
      // INGEN direkte Sol → LTES (al el-baseret energi via HTES)
      // ============================================================

      const updatedSpace = this.storage.getAvailableSpace();

      // Compare effective prices: Solar (no TSO) vs Grid (with TSO)
      const solarEffectivePrice = Math.max(0, spotPrice);

      // ULTRA-STRICT solar purchasing - NEVER above 250 DKK/MWh
      const shouldUseSolar = solarMWh > 0 && solarEffectivePrice < 250;

      let storedForDemand = 0;

      // AL SOL GÅR TIL HTES - ingen direkte LTES
      const canChargeHTES = !this.thermalPhysics || this.thermalPhysics.canChargeHTES();

      if (shouldUseSolar && updatedSpace.htes > 0 && this.priority.canChargeHTES() && canChargeHTES) {
        const solarToHTES = Math.min(
          solarMWh,
          reserveHeaterFor('HTES', getHeaterAvailable()),
          updatedSpace.htes
        );

        if (solarToHTES > 0.01) {
          const stored = this.storage.chargeHTES(solarToHTES, CONSTANTS.EFFICIENCY.heater_to_htes);

          if (this.thermalPhysics) {
            this.thermalPhysics.updateHTESCharge(stored, currentHour);
          }

          heaterUsed += solarToHTES;
          this.priority.commitHeaterUse('HTES', solarToHTES);
          solarUsed += solarToHTES;
          totalCost += solarToHTES * Math.max(0, spotPrice); // No TSO on solar!

          actions.push({
            type: 'OP_CHARGE_HTES_SOLAR',
            amount: solarToHTES,
            stored: stored,
            cost: solarToHTES * Math.max(0, spotPrice),
            category: 'OPERATIONAL',
            reason: 'Solar to HTES (Heliac flow: Sol→Kedel→HTES→Turbine→LTES)'
          });
          this.priority.markHTESCharge();

          // Track stored for demand (vil blive leveret via turbine senere)
          if (unmetDemand > 0) {
            storedForDemand = Math.min(stored * CONSTANTS.EFFICIENCY.turbine_heat, unmetDemand);
          }
        }
      }

      return { totalCost, totalRevenue, heaterUsed, solarUsed, storedForDemand, actions };
    } catch (error) {
      console.error('Error in handleSolarEnergy:', error);
      return { totalCost: 0, totalRevenue: 0, heaterUsed: 0, solarUsed: 0, storedForDemand: 0, actions: [] };
    }
  }

  /**
   * Helper: Håndter grid køb - PROACTIVE STRATEGY
   * @param {number} hour - Current hour
   * @param {number} month - Current month  
   * @param {number} spotPrice - Current spot price
   * @param {number} unmetDemand - Remaining unmet demand
   * @param {Object} levels - Storage levels
   * @param {Object} space - Available storage space
   * @param {number} maxHeater - Maximum heater capacity
   * @returns {Object} Result with cost, heaterUsed and actions
   */
  handleGridPurchase(hour, month, spotPrice, unmetDemand, levels, space, maxHeater) {
    try {
      const actions = [];
      let totalCost = 0;
      let heaterUsed = 0;
      
      const getHeaterAvailable = () => Math.max(0, maxHeater - this.priority.heaterUsedCapacity);
      const reserveHeaterFor = (mode, amount) => {
        return this.priority.requestHeaterUse(mode, amount);
      };

      // DISABLED: Negative price handling - now handled in SimulationEngineV2 BEFORE DemandManager
      // This prevents duplicate charging and ensures negative prices are prioritized first
      // if (spotPrice < 0) { ... }
      
      // Time windows og sæson checks
      const isCheapNight = hour >= 2 && hour <= 4;
      const isWinter = month >= 11 || month <= 2;
      const isSummer = month >= 5 && month <= 8;
      const isVeryCheap = spotPrice < this.thresholds.PRICE_CATEGORIES.VERY_CHEAP;

      // Storage level thresholds - BALANCED for reliable operation
      const minLevel = isWinter ? 0.30 : (isSummer ? 0.20 : this.thresholds.STORAGE_LEVELS.LTES_MIN);
      const targetLevel = isWinter ? 0.70 : (isSummer ? 0.50 : this.thresholds.STORAGE_LEVELS.LTES_TARGET);

      // Emergency checks - reasonable thresholds
      const priceIsAcceptable = spotPrice < this.thresholds.GRID_PURCHASE.MAX_UNMET_DEMAND;
      const isCriticalEmergency = levels.ltes < this.thresholds.STORAGE_LEVELS.LTES_CRITICAL;
      const isEmergency = levels.ltes < minLevel || unmetDemand > 0;
      
      // ADVANCED Forecast-based purchasing strategy
      const forecast = this.priority.forecast;
      let shouldBuyBasedOnForecast = false;
      let forecastUrgency = 0; // 0-1 scale of how urgent charging is
      let forecastReason = '';

      if (forecast && forecast.forecast && forecast.forecast.length > 0) {
        const next6Hours = forecast.forecast.slice(0, 6);
        const next12Hours = forecast.forecast.slice(0, 12);
        const next24Hours = forecast.forecast.slice(0, 24);

        // Calculate upcoming demand and resources
        const demandNext6 = next6Hours.reduce((sum, h) => sum + (h.demandMW || 0), 0);
        const demandNext12 = next12Hours.reduce((sum, h) => sum + (h.demandMW || 0), 0);
        const wasteNext6 = next6Hours.reduce((sum, h) => sum + (h.wasteHeatMW || 0), 0);
        const solarNext6 = next6Hours.reduce((sum, h) => sum + (h.solarMWh || 0), 0);

        // Advanced price analysis
        const avgNext6Price = next6Hours.reduce((sum, h) => sum + h.spotPrice, 0) / next6Hours.length;
        const avgNext12Price = next12Hours.reduce((sum, h) => sum + h.spotPrice, 0) / next12Hours.length;
        const avgNext24Price = next24Hours.reduce((sum, h) => sum + h.spotPrice, 0) / next24Hours.length;
        const maxNext12Price = Math.max(...next12Hours.map(h => h.spotPrice));
        const minNext12Price = Math.min(...next12Hours.map(h => h.spotPrice));

        // Find price valleys and peaks
        const priceValley = next24Hours.findIndex(h => h.spotPrice === Math.min(...next24Hours.map(h => h.spotPrice)));
        const pricePeak = next24Hours.findIndex(h => h.spotPrice === Math.max(...next24Hours.map(h => h.spotPrice)));

        // Estimate storage depletion with safety margin
        const netDemandNext6 = Math.max(0, demandNext6 * 1.1 - wasteNext6 * 0.9 - solarNext6 * 0.8);
        const netDemandNext12 = Math.max(0, demandNext12 * 1.1 - wasteNext6 * 0.9 - solarNext6 * 0.8);
        const currentLTESMWh = levels.ltes * this.storage.storage.ltes.capacity;
        const currentHTESMWh = levels.htes * this.storage.storage.htes.capacity;
        const projectedLTESAfter6h = currentLTESMWh - netDemandNext6;
        const projectedLTESAfter12h = currentLTESMWh - netDemandNext12;

        // Smart decision logic with multiple strategies
        if (spotPrice < minNext12Price * this.thresholds.FORECAST.VALLEY_TOLERANCE) {
          shouldBuyBasedOnForecast = true;
          forecastUrgency = 0.9;
          forecastReason = `Valley price: ${spotPrice} vs next 12h min ${minNext12Price}`;
        } else if (spotPrice < avgNext6Price * this.thresholds.FORECAST.AVG_DISCOUNT) {
          shouldBuyBasedOnForecast = true;
          forecastUrgency = 0.8;
          forecastReason = `15% cheaper than next 6h avg ${avgNext6Price.toFixed(0)}`;
        } else if (projectedLTESAfter6h < this.thresholds.FORECAST.LOW_PREDICTED_STORAGE && spotPrice < this.thresholds.GRID_PURCHASE.MAX_FORECAST_DRIVEN) {
          shouldBuyBasedOnForecast = true;
          forecastUrgency = 0.9;
          forecastReason = `Low storage predicted: ${projectedLTESAfter6h.toFixed(1)} MWh`;
        } else if (priceValley <= 3 && spotPrice < avgNext24Price) {
          shouldBuyBasedOnForecast = true;
          forecastUrgency = 0.7;
          forecastReason = `Near valley in ${priceValley}h`;
        } else if (maxNext12Price > this.thresholds.FORECAST.PEAK_THRESHOLD && spotPrice < this.thresholds.FORECAST.PREP_MAX_PRICE) {
          shouldBuyBasedOnForecast = true;
          forecastUrgency = 0.8;
          forecastReason = `Prep for peak: ${maxNext12Price} DKK coming`;
        } else if (spotPrice < this.thresholds.PRICE_CATEGORIES.CHEAP) {
          shouldBuyBasedOnForecast = true;
          forecastUrgency = 0.6;
          forecastReason = `Excellent price < ${this.thresholds.PRICE_CATEGORIES.CHEAP} DKK`;
        }

        // Dynamic urgency calculation
        if (projectedLTESAfter6h < 0) forecastUrgency = Math.max(forecastUrgency, 1.0);
        else if (projectedLTESAfter6h < 10) forecastUrgency = Math.max(forecastUrgency, 0.85);
        else if (projectedLTESAfter12h < 20) forecastUrgency = Math.max(forecastUrgency, 0.7);
      }
      
      // DYNAMIC grid purchasing based on forecast and current conditions
      // CRITICAL: ALWAYS meet demand regardless of price to ensure 100% coverage
      const shouldBuyGrid = (
        (unmetDemand > 0) || // ALWAYS buy when demand not met - NO PRICE CAP!
        (levels.ltes < minLevel && spotPrice < this.thresholds.GRID_PURCHASE.MAX_MIN_LEVEL) ||
        (levels.ltes < targetLevel && spotPrice < this.thresholds.GRID_PURCHASE.MAX_TARGET_LEVEL) ||
        (levels.htes < this.thresholds.STORAGE_LEVELS.HTES_HIGH && spotPrice < this.thresholds.GRID_PURCHASE.MAX_HTES_FILL) ||
        (isCheapNight && levels.ltes < this.thresholds.STORAGE_LEVELS.LTES_VERY_HIGH && spotPrice < this.thresholds.GRID_PURCHASE.MAX_NIGHT_CHARGE) ||
        (isVeryCheap) || // Always buy when very cheap
        (spotPrice < this.thresholds.PRICE_CATEGORIES.EXCELLENT) || // Aggressive at excellent prices
        (shouldBuyBasedOnForecast && forecastUrgency > this.thresholds.FORECAST.MIN_URGENCY && spotPrice < this.thresholds.GRID_PURCHASE.MAX_FORECAST_DRIVEN) ||
        (spotPrice < 0) // Always buy at negative prices
      );

      // ============================================================
      // SIMPEL STRATEGI: Køb kun til HTES - turbine leverer varme til LTES
      // Flow: Elnet → Kedel → HTES → Turbine (15% el / 85% varme) → LTES → Demand
      // ============================================================

      // HTES charging - To strategier:
      // 1. For demand: Når vi har unmet demand, køb til HTES
      // 2. Smart køb: Ved lave priser, fyld HTES proaktivt til fremtidigt demand

      // Strategy 1: Køb for aktuelt unmet demand
      if (unmetDemand > 0 && space.htes > 0 && this.priority.canChargeHTES() && getHeaterAvailable() > 0) {
        // Vi har unmet demand - lad HTES så turbine kan levere varme
        const htesNeededForDemand = unmetDemand / CONSTANTS.EFFICIENCY.turbine_heat * 1.1; // 10% buffer

        const gridToHTES = Math.min(
          htesNeededForDemand,
          reserveHeaterFor('HTES', getHeaterAvailable()),
          space.htes
        );

        if (gridToHTES > 0.01) {
          const stored = this.storage.chargeHTES(gridToHTES, CONSTANTS.EFFICIENCY.heater_to_htes);
          heaterUsed += gridToHTES;
          this.priority.commitHeaterUse('HTES', gridToHTES);
          this.priority.markHTESCharge();
          totalCost += gridToHTES * (Math.max(0, spotPrice) + CONSTANTS.TSO_AFGIFT);

          actions.push({
            type: 'OP_CHARGE_HTES_GRID',
            amount: gridToHTES,
            stored: stored,
            cost: gridToHTES * (Math.max(0, spotPrice) + CONSTANTS.TSO_AFGIFT),
            category: 'OPERATIONAL',
            reason: `For demand: ${unmetDemand.toFixed(1)} MWh → HTES ${gridToHTES.toFixed(1)} MWh`
          });
        }
      }

      // Strategy 2: SMART PROAKTIVT KØB ved lave priser
      // Dette sikrer vi køber billigt i stedet for dyrt når demand opstår
      // Mål: ~20 GWh HTES årligt for at dække ~17 GWh An-værk demand
      const updatedSpace = this.storage.getAvailableSpace();
      const updatedLevels = this.storage.getLevels();

      // Beregn hvor meget vi proaktivt kan købe baseret på HTES niveau
      // Jo lavere HTES, jo mere aggressivt køber vi ved lave priser
      const htesLevel = updatedLevels.htes;
      const htesCapacity = this.storage.storage.htes.capacity;

      // MAKSIMALT AGGRESSIV BILLIG-KØB STRATEGI
      // Mål: Gns. pris 240-285 DKK/MWh - køb KUN ved billige timer!
      // 2024 data: gns 435 DKK, ~25% timer under 100 DKK, ~3% negative
      // Nøgle: Køb ALTID ved lave priser, selv ved høj HTES - brug det senere!

      // VIGTIG INDSIGT: Vi har ~2760 timer med pris under 300 DKK
      // Vi behøver kun ~1150 timer til 23 GWh - så vi KAN vælge de billigste!

      let shouldBuyProactive = false;
      let proactiveReason = '';
      let proactiveLimit = 0;

      // NOTE: TIER 1 (negative priser) FJERNET - håndteres allerede i SimulationEngineV2.js
      // via CHARGE_HTES_NEGATIVE action for at undgå dobbelt-køb

      // TIER 2: Under 50 DKK - køb ALTID op til 95%
      if (spotPrice >= 0 && spotPrice < 50 && htesLevel < 0.95) {
        shouldBuyProactive = true;
        proactiveReason = `Super billig ${spotPrice.toFixed(0)} DKK`;
        proactiveLimit = htesCapacity * 0.96;
      }
      // TIER 3: Under 100 DKK - køb op til 90%
      else if (spotPrice < 100 && htesLevel < 0.90) {
        shouldBuyProactive = true;
        proactiveReason = `Meget billig ${spotPrice.toFixed(0)} DKK`;
        proactiveLimit = htesCapacity * 0.92;
      }
      // TIER 4: Under 150 DKK - køb op til 80%
      else if (spotPrice < 150 && htesLevel < 0.80) {
        shouldBuyProactive = true;
        proactiveReason = `Billig ${spotPrice.toFixed(0)} DKK`;
        proactiveLimit = htesCapacity * 0.85;
      }
      // TIER 5: Under 200 DKK - køb op til 65%
      else if (spotPrice < 200 && htesLevel < 0.65) {
        shouldBuyProactive = true;
        proactiveReason = `God pris ${spotPrice.toFixed(0)} DKK`;
        proactiveLimit = htesCapacity * 0.72;
      }
      // TIER 6: Under 280 DKK (under gns) - køb op til 50% kun hvis meget lav
      else if (spotPrice < 280 && htesLevel < 0.40) {
        shouldBuyProactive = true;
        proactiveReason = `Acceptabel ${spotPrice.toFixed(0)} DKK`;
        proactiveLimit = htesCapacity * 0.55;
      }
      // TIER 7: Under 350 DKK - kun ved kritisk lav HTES
      else if (spotPrice < 350 && htesLevel < 0.20) {
        shouldBuyProactive = true;
        proactiveReason = `HTES kritisk lav ${(htesLevel*100).toFixed(0)}%`;
        proactiveLimit = htesCapacity * 0.35;
      }

      if (shouldBuyProactive && updatedSpace.htes > 1 && this.priority.canChargeHTES() && getHeaterAvailable() > 0) {
        // Beregn hvor meget vi vil købe - max op til proactiveLimit
        const currentHTES = htesLevel * htesCapacity;
        const targetHTES = Math.min(proactiveLimit, currentHTES + updatedSpace.htes);
        const proactiveAmount = Math.min(
          targetHTES - currentHTES,
          reserveHeaterFor('HTES', getHeaterAvailable()),
          updatedSpace.htes,
          20 // Max 20 MWh per time for at undgå over-køb
        );

        if (proactiveAmount > 0.5) {
          const stored = this.storage.chargeHTES(proactiveAmount, CONSTANTS.EFFICIENCY.heater_to_htes);
          heaterUsed += proactiveAmount;
          this.priority.commitHeaterUse('HTES', proactiveAmount);
          this.priority.markHTESCharge();
          totalCost += proactiveAmount * (Math.max(0, spotPrice) + CONSTANTS.TSO_AFGIFT);

          actions.push({
            type: 'PROACTIVE_HTES_CHARGE',
            amount: proactiveAmount,
            stored: stored,
            cost: proactiveAmount * (Math.max(0, spotPrice) + CONSTANTS.TSO_AFGIFT),
            category: 'OPERATIONAL',
            reason: proactiveReason
          });
        }
      }
      
      // Track how much was stored for demand
      let storedForDemand = 0;
      // Find the amount stored for operational demand from actions
      actions.forEach(action => {
        if (action.type === 'OP_CHARGE_LTES_GRID' && action.stored) {
          storedForDemand += action.stored;
        }
      });
      
      return { totalCost, heaterUsed, storedForDemand, actions };
    } catch (error) {
      console.error('Error in handleGridPurchase:', error);
      return { totalCost: 0, heaterUsed: 0, storedForDemand: 0, actions: [] };
    }
  }

  /**
   * Helper: Kør turbine operation - PROACTIVE STRATEGY
   * @param {number} hour - Current hour
   * @param {number} spotPrice - Current spot price
   * @param {number} unmetDemand - Remaining unmet demand
   * @param {number} wasteHeatMW - Available waste heat
   * @param {number} solarMWh - Available solar energy
   * @param {Object} levels - Storage levels
   * @returns {Object} Result with revenue and actions
   */
  runTurbineOperation(hour, spotPrice, unmetDemand, wasteHeatMW, solarMWh, levels) {
    try {
      const actions = [];
      let totalRevenue = 0;
      
      // OPTIMIZED turbine thresholds - lower for better economics
      // Now that HTES charging is improved (70%/90% thresholds), turbine can run at lower prices
      const isDischargeWindow = hour >= 17 && hour < 21;
      const isPeakPrice = spotPrice > 220;  // ✅ Lowered 250→220 for more turbine usage
      const isGoodArbitrage = spotPrice > 180;  // Match waste heat cost (150 + margin)
      const isExcellentPrice = spotPrice > 400; // Premium price level
      
      // Priority 1: Always run if we need heat
      const needsHeatFromHTES = unmetDemand > 0 && levels.htes > 0.02;
      
      // Priority 2: CONSERVATIVE space creation - DON'T waste HTES on space creation!
      // ONLY create space when absolutely necessary AND price is low
      const needsSpaceForCheapEnergy = (
        (levels.ltes > 0.95 && wasteHeatMW > 5 && spotPrice < 100) || // CRITICAL: 95% full, cheap price
        (levels.ltes > 0.90 && spotPrice < 30) || // Very cheap hours when nearly full
        (levels.ltes > 0.85 && spotPrice < 0) // Negative prices with high LTES
      );
      
      // Priority 3: DEAKTIVERET - Turbine kører KUN for demand, ikke arbitrage
      // Al turbine drift for demand håndteres i deliverFromStorage()
      // Arbitrage el-salg sker automatisk når turbine kører for demand (15% el split)
      const profitableArbitrage = false; // DEAKTIVERET
      
      // Priority 4: DEAKTIVERET - Ingen proaktiv turbine discharge
      // Turbine kører KUN når der er demand der skal dækkes
      const proactiveDischarge = false; // DEAKTIVERET
      const forecastReason = '';
      
      const shouldRunTurbine = needsHeatFromHTES || needsSpaceForCheapEnergy || profitableArbitrage || proactiveDischarge;
      
      if (shouldRunTurbine && levels.htes > 0.02 && this.priority.canDischargeHTES()) {
        const htesAvailable = (levels.htes - 0.02) * this.storage.storage.htes.capacity;
        
        // Dynamic turbine capacity based on situation
        let turbineCapacity = 5; // Default
        if (needsHeatFromHTES) {
          turbineCapacity = Math.min(10, unmetDemand / CONSTANTS.EFFICIENCY.turbine_heat * 1.2);
        } else if (needsSpaceForCheapEnergy) {
          // Calculate how much space we need
          const spaceNeeded = Math.max(wasteHeatMW, solarMWh) * 1.5;
          turbineCapacity = Math.min(10, spaceNeeded / CONSTANTS.EFFICIENCY.turbine_heat);
        } else if (isPeakPrice) {
          turbineCapacity = 10; // Max at peak prices
        } else if (spotPrice > 200) {
          turbineCapacity = 8;
        } else if (spotPrice > 150) {
          turbineCapacity = 6;
        }
        
        const htesDischarge = Math.min(turbineCapacity, htesAvailable);
        if (htesDischarge > 0.1) {
          const discharged = this.storage.dischargeHTES(htesDischarge);
          const heat = discharged * CONSTANTS.EFFICIENCY.turbine_heat;
          const electricity = discharged * CONSTANTS.EFFICIENCY.turbine_electric;
          
          const storedInLTES = this.storage.chargeLTES(heat);
          totalRevenue += electricity * spotPrice;
          
          let reason = 'Unknown';
          if (needsHeatFromHTES) reason = 'Heat production for demand';
          else if (needsSpaceForCheapEnergy) reason = `Space for ${wasteHeatMW > 2 ? `${wasteHeatMW.toFixed(1)}MW waste` : `${solarMWh.toFixed(1)}MWh solar`}`;
          else if (isExcellentPrice) reason = `Premium arbitrage at ${spotPrice.toFixed(0)} DKK/MWh`;
          else if (profitableArbitrage) reason = `Arbitrage at ${spotPrice.toFixed(0)} DKK/MWh`;
          else if (proactiveDischarge) reason = `Forecast: ${forecastReason}`;
          
          actions.push({
            type: 'DISCHARGE_HTES_TURBINE',
            input: discharged,
            heat: heat,
            electricity: electricity,
            storedInLTES: storedInLTES,
            revenue: electricity * spotPrice,
            category: 'OPERATIONAL',
            reason: reason
          });
        }
      }
      
      return { totalRevenue, actions };
    } catch (error) {
      console.error('Error in runTurbineOperation:', error);
      return { totalRevenue: 0, actions: [] };
    }
  }

  /**
   * Helper: Udfør arbitrage strategier (sommer og HTES)
   * @param {number} hour - Current hour
   * @param {number} month - Current month
   * @param {number} spotPrice - Current spot price
   * @param {number} solarMWh - Available solar energy
   * @param {Object} levels - Storage levels
   * @param {Object} space - Available storage space
   * @param {number} maxHeater - Maximum heater capacity
   * @returns {Object} Result with cost, heaterUsed, solarUsed and actions
   */
  executeArbitrage(hour, month, spotPrice, solarMWh, levels, space, maxHeater) {
    try {
      const actions = [];
      let totalCost = 0;
      let heaterUsed = 0;
      let solarUsed = 0;

      // ============================================================
      // HELIAC FLOW: Al el-baseret energi via HTES
      // Sommer sol-strategi bruger nu HTES i stedet for direkte LTES
      // ARBITRAGE = KUN batteri (3MW capacity, 366 cycles/year)
      // ============================================================

      const getHeaterAvailable = () => Math.max(0, maxHeater - this.priority.heaterUsedCapacity);
      const reserveHeaterFor = (mode, amount) => {
        return this.priority.requestHeaterUse(mode, amount);
      };

      // Sommer strategi - nu via HTES
      const isSummer = month >= 5 && month <= 8;
      if (isSummer && solarMWh > 0 && spotPrice < 200) {
        const canChargeHTES = !this.thermalPhysics || this.thermalPhysics.canChargeHTES();

        if (space.htes > 0 && this.priority.canChargeHTES() && canChargeHTES && getHeaterAvailable() > 0) {
          const solarToHTES = Math.min(
            solarMWh,
            reserveHeaterFor('HTES', getHeaterAvailable()),
            space.htes
          );

          if (solarToHTES > 0.01) {
            const stored = this.storage.chargeHTES(solarToHTES, CONSTANTS.EFFICIENCY.heater_to_htes);
            heaterUsed += solarToHTES;
            this.priority.commitHeaterUse('HTES', solarToHTES);
            solarUsed += solarToHTES;
            totalCost += solarToHTES * Math.max(0, spotPrice);

            actions.push({
              type: 'OP_CHARGE_HTES_SOLAR',
              amount: solarToHTES,
              stored: stored,
              cost: solarToHTES * Math.max(0, spotPrice),
              category: 'OPERATIONAL',
              reason: 'Summer solar via HTES (Heliac flow)'
            });
            this.priority.markHTESCharge();
          }
        }
      }

      return { totalCost, heaterUsed, solarUsed, actions };
    } catch (error) {
      console.error('Error in executeArbitrage:', error);
      return { totalCost: 0, heaterUsed: 0, solarUsed: 0, actions: [] };
    }
  }

  /**
   * Helper: Beregn finansielle resultater
   * @param {Object} results - Accumulated results from all operations
   * @returns {Object} Financial calculations
   */
  calculateFinancials(results) {
    try {
      // Consolidate all financial data
      const financials = {
        totalCost: results.totalCost || 0,
        totalRevenue: results.totalRevenue || 0,
        netResult: (results.totalRevenue || 0) - (results.totalCost || 0)
      };
      
      return financials;
    } catch (error) {
      console.error('Error in calculateFinancials:', error);
      return {
        totalCost: 0,
        totalRevenue: 0,
        netResult: 0
      };
    }
  }

  /**
   * Main demand meeting function - orchestrates all helper methods
   * @param {number} demand - Heat demand in MWh
   * @param {number} hour - Current hour (0-23)
   * @param {number} spotPrice - Current electricity spot price
   * @param {number} wasteHeatMW - Available waste heat from factory
   * @param {number} solarMWh - Available solar energy
   * @param {number} month - Current month (1-12)
   * @returns {Object} Result object with actions, production, costs, etc.
   */
  meetDemand(demand, hour, spotPrice, wasteHeatMW, solarMWh, month, currentHour = 0) {
    // PERFORMANCE: Only log every 1000 hours to avoid browser slowdown
    const shouldLog = currentHour % 1000 === 0;
    if (shouldLog) {
      console.log('meetDemand START with demand:', demand);
    }
    
    try {
      // Initialize result tracking
      let actions = [];
      let totalCost = 0;
      let totalRevenue = 0;
      let anProduction = 0;
      let heaterUsed = 0;
      
      // Get storage info
      const storageInfo = this.getStorageInfo();
      if (!storageInfo) {
        return {
          actions: [],
          anProduction: 0,
          unmetDemand: demand,
          totalCost: 0,
          totalRevenue: 0,
          heaterUsed: 0
        };
      }
      
      const { levels, space } = storageInfo;
      if (shouldLog) {
        console.log('Levels:', levels);
        console.log('Space:', space);
      }
      
      // Heater capacity management
      const maxHeater = this.priority.constraints.heater_max_MW || 30;
      
      // ========================================
      // STEP 1: LEVER DEMAND FRA LTES (ALTID!)
      // ========================================
      const deliveryResult = this.deliverFromStorage(demand, levels, currentHour);
      anProduction = deliveryResult.anProduction;
      actions = actions.concat(deliveryResult.actions);
      
      let unmetDemand = Math.max(0, demand - anProduction);
      
      // ========================================
      // STEP 2: HÅNDTER OVERSKUDSVARME
      // ========================================
      const wasteHeatResult = this.handleWasteHeat(wasteHeatMW, levels, space, spotPrice, currentHour);
      totalCost += wasteHeatResult.totalCost;
      totalRevenue += wasteHeatResult.totalRevenue;
      actions = actions.concat(wasteHeatResult.actions);
      
      // ========================================
      // STEP 3: HÅNDTER SOL ENERGI
      // ========================================
      const solarResult = this.handleSolarEnergy(solarMWh, spotPrice, levels, maxHeater, unmetDemand, hour, currentHour);
      totalCost += solarResult.totalCost;
      totalRevenue += solarResult.totalRevenue;
      heaterUsed += solarResult.heaterUsed;
      const remainingSolar = Math.max(0, solarMWh - (solarResult.solarUsed || 0));
      actions = actions.concat(solarResult.actions);
      
      // Update unmet demand after solar
      if (solarResult.storedForDemand > 0) {
        unmetDemand = Math.max(0, unmetDemand - solarResult.storedForDemand);
      }
      
      // ========================================
      // STEP 4: HÅNDTER GRID KØB OG OPLADNING
      // ========================================
      const gridResult = this.handleGridPurchase(hour, month, spotPrice, unmetDemand, levels, space, maxHeater);
      totalCost += gridResult.totalCost;
      heaterUsed += gridResult.heaterUsed;
      actions = actions.concat(gridResult.actions);
      
      // Update unmet demand after grid
      if (gridResult.storedForDemand > 0) {
        unmetDemand = Math.max(0, unmetDemand - gridResult.storedForDemand);
      }
      
      // ========================================
      // STEP 5: KØR TURBINE OPERATION
      // ========================================
      const turbineResult = this.runTurbineOperation(hour, spotPrice, unmetDemand, wasteHeatMW, remainingSolar, levels);
      totalRevenue += turbineResult.totalRevenue;
      actions = actions.concat(turbineResult.actions);
      
      // ========================================
      // STEP 6: UDFØR ARBITRAGE STRATEGIER
      // ========================================
      const arbitrageResult = this.executeArbitrage(hour, month, spotPrice, remainingSolar, levels, space, maxHeater);
      totalCost += arbitrageResult.totalCost;
      heaterUsed += arbitrageResult.heaterUsed;
      actions = actions.concat(arbitrageResult.actions);
      
      // ========================================
      // STEP 7: BEREGN FINANSIELLE RESULTATER
      // ========================================
      const financials = this.calculateFinancials({
        totalCost,
        totalRevenue
      });
      
      // Final unmet demand check - Brug HTES→Turbine flow for at dække demand
      // Dette er det KORREKTE flow ifølge Heliac specs:
      // Elnet → Kedel → HTES → Turbine (15% el / 85% varme) → LTES → DH
      unmetDemand = Math.max(0, demand - anProduction);
      if (unmetDemand > 0.01) {
        const finalLevels = this.storage.getLevels();
        const finalSpace = this.storage.getAvailableSpace();

        // EMERGENCY FLOW via HTES→Turbine (korrekt Heliac flow)
        // Køb el → lad HTES → kør turbine → varme til LTES → dæk demand
        const htesNeededForDemand = unmetDemand / CONSTANTS.EFFICIENCY.turbine_heat;

        // Kan vi dække fra eksisterende HTES?
        const htesAvailable = finalLevels.htes * this.storage.storage.htes.capacity;

        if (htesAvailable > htesNeededForDemand * 0.5) {
          // Brug eksisterende HTES
          const htesDischarge = Math.min(htesNeededForDemand, htesAvailable * 0.95);
          const discharged = this.storage.dischargeHTES(htesDischarge);
          const turbineHeat = discharged * CONSTANTS.EFFICIENCY.turbine_heat;
          const turbineEl = discharged * CONSTANTS.EFFICIENCY.turbine_electric;

          // VIGTIGT: Lever varme DIREKTE til demand (IKKE via LTES!)
          // Dette undgår dobbelt-tælling - varme tælles KUN når den leveres
          const deliveredHeat = Math.min(turbineHeat, unmetDemand);
          anProduction += deliveredHeat;
          totalRevenue += turbineEl * spotPrice;

          // Evt. overskudsvarme kan gå til LTES buffer (men tælles IKKE som anProduction)
          const excessHeat = turbineHeat - deliveredHeat;
          if (excessHeat > 0.01) {
            this.storage.chargeLTES(excessHeat);
          }

          // Calculate losses for delivered heat
          const abPlantDelivery = deliveredHeat * (1 - CONSTANTS.EFFICIENCY.production_loss);
          const netDeliveryAmount = abPlantDelivery * (1 - CONSTANTS.EFFICIENCY.grid_loss);

          actions.push({
            type: 'EMERGENCY_HTES_TO_DEMAND',
            input: discharged,
            heat: turbineHeat,
            deliveredHeat: deliveredHeat,
            excessToLTES: excessHeat,
            electricity: turbineEl,
            anPlant: deliveredHeat,
            abPlant: abPlantDelivery,
            netDelivery: netDeliveryAmount,
            revenue: turbineEl * spotPrice,
            reason: `Emergency HTES→Demand: ${deliveredHeat.toFixed(2)} MWh direkte`
          });

          unmetDemand = Math.max(0, demand - anProduction);
        }

        // Hvis stadig unmet demand, køb el til HTES og kør turbine med det samme
        unmetDemand = Math.max(0, demand - anProduction);
        if (unmetDemand > 0.01) {
          const htesNeeded = unmetDemand / CONSTANTS.EFFICIENCY.turbine_heat * 1.1;
          const heaterAvailable = Math.max(0, (this.priority.constraints.heater_max_MW || 30) - this.priority.heaterUsedCapacity);
          const emergencyPurchase = Math.min(htesNeeded, heaterAvailable, finalSpace.htes);

          if (emergencyPurchase > 0.1) {
            // Køb el til HTES
            const stored = this.storage.chargeHTES(emergencyPurchase, CONSTANTS.EFFICIENCY.heater_to_htes);
            const purchaseCost = emergencyPurchase * (Math.max(0, spotPrice) + CONSTANTS.TSO_AFGIFT);
            totalCost += purchaseCost;
            heaterUsed += emergencyPurchase;

            // Kør turbine med det samme
            const discharged = this.storage.dischargeHTES(stored);
            const turbineHeat = discharged * CONSTANTS.EFFICIENCY.turbine_heat;
            const turbineEl = discharged * CONSTANTS.EFFICIENCY.turbine_electric;

            // VIGTIGT: Lever varme DIREKTE til demand (IKKE via LTES!)
            // Dette undgår dobbelt-tælling - varme tælles KUN når den leveres
            const deliveredHeat = Math.min(turbineHeat, unmetDemand);
            anProduction += deliveredHeat;
            totalRevenue += turbineEl * spotPrice;

            // Evt. overskudsvarme kan gå til LTES buffer (men tælles IKKE som anProduction)
            const excessHeat = turbineHeat - deliveredHeat;
            if (excessHeat > 0.01) {
              this.storage.chargeLTES(excessHeat);
            }

            // Calculate losses for delivered heat
            const abPlantDelivery = deliveredHeat * (1 - CONSTANTS.EFFICIENCY.production_loss);
            const netDeliveryAmount = abPlantDelivery * (1 - CONSTANTS.EFFICIENCY.grid_loss);

            actions.push({
              type: 'EMERGENCY_GRID_TO_HTES_TO_DEMAND',
              purchasedMWh: emergencyPurchase,
              htesStored: stored,
              htesInput: discharged,
              heat: turbineHeat,
              deliveredHeat: deliveredHeat,
              excessToLTES: excessHeat,
              electricity: turbineEl,
              cost: purchaseCost,
              revenue: turbineEl * spotPrice,
              anPlant: deliveredHeat,
              abPlant: abPlantDelivery,
              netDelivery: netDeliveryAmount,
              reason: `Emergency: El→HTES→Turbine→Demand @ ${spotPrice.toFixed(0)} DKK`
            });

            unmetDemand = Math.max(0, demand - anProduction);
          }
        }
      }
      
      // Return result
      const result = {
        actions,
        anProduction,
        unmetDemand,
        totalCost: financials.totalCost,
        totalRevenue: financials.totalRevenue,
        heaterUsed
      };

      if (shouldLog) {
        console.log('meetDemand END, returning:', result);
      }
      return result;
      
    } catch (error) {
      console.error('ERROR in meetDemand:', error);
      console.error('Stack:', error.stack);
      
      // Always return a valid object on error
      return {
        actions: [],
        anProduction: 0,
        unmetDemand: demand || 0,
        totalCost: 0,
        totalRevenue: 0,
        heaterUsed: 0
      };
    }
  }
}

export default ImprovedDemandManager;